import classNames from 'classnames';
import React, { useState, useEffect, useRef, useLayoutEffect, useMemo } from 'react';

import Tab from './Tab';
import TabButton from './TabButton';
import TabList from './TabList';
import { TabsComposition, TabsProps } from './TabsTypes';
import { TabContext, TabsContextProps } from './hooks/useTabContext';

import './tabs.scss';

type RootFunctionComponent<OwnProps, SubComponents> = React.FunctionComponent<OwnProps> & SubComponents;

const Tabs: RootFunctionComponent<TabsProps, TabsComposition> = <TValue = string,>({
    className,
    children,
    defaultValue,
    screenReaderTitle,
    onChangeValue,
    removeHiddenTabsFromDom = true,
    ...rest
}: TabsProps<TValue>) => {
    const [selectedValue, setSelectedValue] = useState(defaultValue);
    const [tabList, setTabList] = useState<HTMLDivElement | null>(null);

    const valueChange = useRef(onChangeValue);

    useLayoutEffect(() => {
        if (!selectedValue) {
            const element =
                (document.getElementById(String(selectedValue)) as HTMLButtonElement | null) ??
                (tabList?.firstElementChild as HTMLButtonElement | null) ??
                null;
            setSelectedValue((element?.id as TValue) ?? defaultValue);
        }
    }, [tabList, defaultValue, selectedValue]);

    useEffect(() => {
        if (selectedValue && valueChange.current) {
            valueChange.current(selectedValue);
        }
    }, [selectedValue, valueChange]);

    const contextValue = useMemo(
        () => ({
            value: selectedValue,
            setValue: setSelectedValue,
            setTabList,
            removeHiddenTabsFromDom,
        }),

        [selectedValue, setSelectedValue, setTabList, removeHiddenTabsFromDom],
    );

    return (
        <TabContext.Provider value={contextValue as TabsContextProps<string>}>
            <div className={classNames('sgw-tabs', className)} {...rest}>
                {screenReaderTitle && (
                    <span className="sgw-tabs__screen-reader-title screen-reader-text">{screenReaderTitle}</span>
                )}
                {children}
            </div>
        </TabContext.Provider>
    );
};

Tabs.TabButton = TabButton;
Tabs.TabList = TabList;
Tabs.Tab = Tab;

export default Tabs;
